// components/shared/notifications/EmailForm.tsx
import React from "react";
import { Mail, Upload, X } from "lucide-react";

interface EmailFormProps {
  subject: string;
  message: string;
  attachments?: File[];
  attachmentPreviews: string[];
  onSubjectChange: (value: string) => void;
  onMessageChange: (value: string) => void;
  onAttachmentUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveAttachment: (index: number) => void;
  onSubmit: (e: React.FormEvent) => void;
  sending: boolean;
  userCount: number;
  isBroadcast: boolean;
  onToggleMode: () => void;
}

export const EmailForm: React.FC<EmailFormProps> = ({
  subject,
  message,
  attachments,
  attachmentPreviews,
  onSubjectChange,
  onMessageChange,
  onAttachmentUpload,
  onRemoveAttachment,
  onSubmit,
  sending,
  userCount,
  isBroadcast,
  onToggleMode,
}) => (
  <div className="bg-white rounded-lg shadow-md p-6">
    <div className="flex justify-between items-center mb-4">
      <h3 className="text-lg font-semibold">
        {isBroadcast ? "Email à tous les utilisateurs" : `Email (${userCount} utilisateur${userCount > 1 ? "s" : ""})`}
      </h3>
      {isBroadcast && (
        <button
          onClick={onToggleMode}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-lg transition-colors hover:bg-gray-300"
        >
          Envoyer à un seul utilisateur
        </button>
      )}
    </div>

    <form onSubmit={onSubmit} className="space-y-4">
      <div>
        <label className="block mb-2 text-sm font-medium text-gray-700">Sujet *</label>
        <input
          type="text"
          value={subject}
          onChange={(e) => onSubjectChange(e.target.value)}
          className="px-4 py-2 w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-500 focus:border-green-500"
          placeholder="Ex: Nouvelle mise à jour importante"
          required
        />
      </div>

      <div>
        <label className="block mb-2 text-sm font-medium text-gray-700">Message *</label>
        <textarea
          value={message}
          onChange={(e) => onMessageChange(e.target.value)}
          rows={6}
          className="px-4 py-2 w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-500 focus:border-green-500"
          placeholder="Rédigez votre email ici..."
          required
        />
      </div>

      <div>
        <label className="block mb-2 text-sm font-medium text-gray-700">
          Pièces jointes (optionnel - max 10MB par fichier, 20MB total)
        </label>
        <div className="p-6 text-center rounded-lg border-2 border-gray-300 border-dashed transition-colors hover:border-green-500">
          <input type="file" multiple onChange={onAttachmentUpload} className="hidden" id="attachment-upload" />
          <label htmlFor="attachment-upload" className="flex flex-col items-center cursor-pointer">
            <Upload className="mb-2 w-12 h-12 text-gray-400" />
            <p className="text-sm font-medium text-gray-600">Cliquez pour télécharger des fichiers</p>
            <p className="mt-1 text-xs text-gray-500">Tous formats, max 10MB par fichier</p>
          </label>
        </div>

        {attachments && attachments.length > 0 && (
          <div className="mt-4 space-y-2">
            <p className="text-sm font-medium text-gray-700">{attachments.length} fichier(s) attaché(s)</p>
            {attachments.map((file, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 rounded-lg bg-gray-50 border border-gray-200"
              >
                <div className="flex items-center gap-3">
                  {file.type.startsWith("image/") && attachmentPreviews[index] && (
                    <img
                      src={attachmentPreviews[index]}
                      alt={`Preview ${index}`}
                      className="object-cover w-10 h-10 rounded"
                    />
                  )}
                  <div>
                    <p className="text-sm font-medium text-gray-900">{file.name}</p>
                    <p className="text-xs text-gray-500">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => onRemoveAttachment(index)}
                  className="p-1 text-red-600 transition-colors hover:text-red-700"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
        <p className="mb-3 text-sm font-medium text-gray-700">Aperçu:</p>
        <div className="p-4 bg-white rounded-lg shadow-sm">
          <div className="space-y-4">
            <div>
              <p className="text-sm font-medium text-gray-500">De: noreply@votredomaine.com</p>
              <p className="text-sm font-medium text-gray-500">
                À: {isBroadcast ? "Tous les utilisateurs" : `${userCount} utilisateur(s)`}
              </p>
              <p className="text-sm font-medium text-gray-500">Sujet: {subject || "Votre sujet ici"}</p>
            </div>
            <div className="pt-4 border-t">
              <p className="text-gray-700 whitespace-pre-wrap">{message || "Votre message ici"}</p>
            </div>
            {attachments && attachments.length > 0 && (
              <div className="pt-4 border-t">
                <p className="mb-2 text-sm font-medium text-gray-700">Pièces jointes:</p>
                <div className="flex flex-wrap gap-2">
                  {attachments.map((file, index) => (
                    <div key={index} className="px-3 py-1 text-sm bg-gray-100 rounded border border-gray-300">
                      {file.name}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <button
        type="submit"
        disabled={sending}
        className="flex gap-2 justify-center items-center py-3 w-full font-medium text-white bg-green-600 rounded-lg transition-colors hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
      >
        {sending ? (
          <>
            <div className="w-5 h-5 rounded-full border-2 border-white animate-spin border-t-transparent" />
            Envoi en cours...
          </>
        ) : (
          <>
            <Mail className="w-5 h-5" />
            {isBroadcast
              ? "Envoyer à tous les utilisateurs"
              : `Envoyer à ${userCount} utilisateur${userCount > 1 ? "s" : ""}`}
          </>
        )}
      </button>
    </form>
  </div>
);
